import { Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button as MuiButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DateField,
  Labeled,
  NumberField,
  ReferenceField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  useGetMany,
  useGetOne,
  useRecordContext
} from 'react-admin';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const FilePreview = () => {
  const record = useRecordContext();

  if (!record || !record.filePath) return null;

  // Abre em nova aba (única forma confiável já que a URL não tem extensão)
  const handleOpenNewTab = () => {
    window.open(record.filePath, '_blank');
  };

  return (
    <Box sx={{ mt: 1 }}>
      <MuiButton
        onClick={handleOpenNewTab}
        startIcon={<OpenInNewIcon />}
        variant="outlined"
        color="primary"
      >
        Visualizar Comprovante
      </MuiButton>
    </Box>
  );
};

const DetailsTab = () => {
  const record = useRecordContext();

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Labeled label="Revenda">
            <ReferenceField
              source="retailerId"
              reference="retailers"
              link={false}
              emptyText="Não informado"
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid size={12}>
          <Labeled label="Data do Pagamento">
            <DateField
              source="paymentDate"
              showTime
              locales="pt-BR"
            />
          </Labeled>
        </Grid>
        <Grid size={12}>
          <Labeled label="Valor Total">
            <NumberField
              source="totalValue"
              options={{
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }}
            />
          </Labeled>
        </Grid>
        <Grid size={12}>
          <Labeled label="Qtd. Cobranças">
            <Typography component="span">{record?.clientBills?.length || 0}</Typography>
          </Labeled>
        </Grid>
        <Grid size={12}>
          <Labeled label="Criado por">
            <ReferenceField
              source="createdBy"
              reference="employees"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        {record?.observation && (
          <Grid size={12}>
            <Labeled label="Observação">
              <Typography component="span">{record.observation}</Typography>
            </Labeled>
          </Grid>
        )}
        {record?.filePath && (
          <Grid size={12}>
            <Labeled label="Comprovante">
              <FilePreview />
            </Labeled>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

const BillsTab = () => {
  const record = useRecordContext();

  // Busca dados completos do pagamento (o cache da lista não tem clientBills)
  const { data: fullRecord, isLoading } = useGetOne(
    'partner-payments',
    { id: record?.id },
    { enabled: !!record?.id }
  );

  const clientBills = fullRecord?.clientBills || [];

  const chainIds = [...new Set(clientBills.map(b => b.chainId).filter(Boolean))];
  const stationIds = [...new Set(clientBills.map(b => b.placeId).filter(Boolean))];

  const { data: chains } = useGetMany('chains', { ids: chainIds }, { enabled: chainIds.length > 0 });
  const { data: stations } = useGetMany('places', { ids: stationIds }, { enabled: stationIds.length > 0 });

  const getChainName = (chainId) => chains?.find(c => c.id === chainId)?.name || '-';
  const getStationName = (placeId) => stations?.find(s => s.id === placeId)?.name || '-';

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography color="textSecondary">Carregando...</Typography>
      </Paper>
    );
  }

  if (!clientBills.length) {
    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography color="textSecondary">Nenhuma cobrança associada</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Rede</TableCell>
            <TableCell>Posto</TableCell>
            <TableCell>Competência</TableCell>
            <TableCell align="right">Valor Parceiro</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {clientBills.map((bill, index) => (
            <TableRow key={bill.id || index}>
              <TableCell>{getChainName(bill.chainId)}</TableCell>
              <TableCell>{getStationName(bill.placeId)}</TableCell>
              <TableCell>{bill.monthDate || '-'}</TableCell>
              <TableCell align="right">{formatCurrency(bill.retailerValue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const PartnerPaymentShow = props => {
  return (
    <Show {...props} resource="partner-payments" title="Pagamento de Parceiro">
      <TabbedShowLayout>
        <Tab label="Detalhes">
          <DetailsTab />
        </Tab>
        <Tab label="Cobranças">
          <BillsTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export default PartnerPaymentShow;
